// src/components/useSpeechRecognition.js
import { useMemo, useRef, useState, useCallback } from "react";

/**
 * Hook simples para reconhecimento de voz (modo one-shot).
 * - Cria o SpeechRecognition no `iniciar()` e destrói no `onend`.
 * - Junta apenas resultados finais e chama `onTextoReconhecido(texto)` no fim.
 *
 * @param {(texto: string) => void} onTextoReconhecido callback com o texto final da sessão
 * @param {{
 *   lang?: string,
 *   interimResults?: boolean,   // default false
 *   continuous?: boolean        // default true
 * }} opts
 */
export function useSpeechRecognition(onTextoReconhecido, opts = {}) {
  const { lang = "pt-PT", interimResults = false, continuous = true } = opts;

  const suporteVoz = useMemo(
    () =>
      typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition),
    []
  );

  const [reconhecendo, setReconhecendo] = useState(false);
  const [erro, setErro] = useState(null);

  const recogRef = useRef(null);
  const bufferRef = useRef(""); // acumula o texto final desta sessão

  const iniciar = useCallback(() => {
    if (!suporteVoz || reconhecendo) return;

    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    try {
      const rec = new Ctor();
      rec.lang = lang;
      rec.interimResults = interimResults; // por omissão false
      rec.maxAlternatives = 1;
      rec.continuous = continuous;

      bufferRef.current = "";
      setErro(null);

      rec.onstart = () => setReconhecendo(true);

      rec.onresult = (event) => {
        // Junta apenas resultados finais desta sessão
        // (alguns motores enviam múltiplos "finals" numa mesma sessão)
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (!r?.isFinal) continue;
          const part = (r[0]?.transcript || "").trim();
          if (part) {
            bufferRef.current = bufferRef.current ? `${bufferRef.current} ${part}` : part;
          }
        }
      };

      rec.onerror = (ev) => {
        setErro(ev?.error || "unknown");
        setReconhecendo(false);
        try {
          rec.stop();
        } catch {}
        recogRef.current = null;
      };

      rec.onend = () => {
        setReconhecendo(false);
        recogRef.current = null;
        const finalText = bufferRef.current.trim();
        bufferRef.current = "";
        if (finalText && typeof onTextoReconhecido === "function") {
          onTextoReconhecido(finalText);
        }
      };

      rec.start(); // precisa de gesto do utilizador
      recogRef.current = rec;
    } catch (e) {
      setErro("start-failed");
      setReconhecendo(false);
      recogRef.current = null;
    }
  }, [suporteVoz, reconhecendo, lang, interimResults, continuous, onTextoReconhecido]);

  const parar = useCallback(() => {
    const rec = recogRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch {}
    // o commit acontece no onend
  }, []);

  return { iniciar, parar, reconhecendo, suporteVoz, erro };
}
